import type * as zero from "@rocicorp/zero"
import type * as t from "../generated/schema"
import { assert } from "./assert.ts"
import type { AuthData } from "./auth.ts"
import { assertChannelPermission, assertIsLoggedIn } from "./auth.ts"
import type { schema } from "./schema.ts"

export function createMutators(authData: AuthData | null | undefined) {
  return {
    channel_subscription: {
      async markRead(
        tx: Transaction,
        args: { id: string; channelId: string; now: number },
      ) {
        assertIsLoggedIn(authData)
        await assertChannelPermission(
          tx,
          authData,
          args.channelId,
          "channel.view",
        )

        const subscription = await tx.query.channel_subscription
          .where("id", args.id)
          .one()
          .run()
        if (subscription)
          await tx.mutate.channel_subscription.update({
            id: subscription.id,
            last_read_at: args.now,
          })
        else {
          await tx.mutate.channel_subscription.insert({
            account_id: authData.id,
            channel_id: args.channelId,
            id: args.id,
            inserted_at: args.now,
            last_read_at: args.now,
            subscribed_at: args.now,
            updated_at: args.now,
          })
        }
      },
      async subscribe(
        tx: Transaction,
        args: { id: string; channelId: string },
      ) {
        const now = Date.now()
        assertIsLoggedIn(authData)
        await assertChannelPermission(
          tx,
          authData,
          args.channelId,
          "channel.view",
        )
        await tx.mutate.channel_subscription.upsert({
          account_id: authData.id,
          channel_id: args.channelId,
          id: args.id,
          inserted_at: now,
          last_read_at: now,
          subscribed_at: now,
          updated_at: now,
        })
      },
      async unsubscribe(tx: Transaction, args: { id: string }) {
        assertIsLoggedIn(authData)
        const subscription = await tx.query.channel_subscription
          .where("id", args.id)
          .one()
          .run()
        assert(
          subscription?.account_id === authData.id,
          "forbidden:channel_subscription",
        )
        await tx.mutate.channel_subscription.update({
          id: args.id,
          subscribed_at: null,
        })
      },
    },
    message: {
      async create(
        tx: Transaction,
        args: {
          id: string
          attachments: Pick<
            t.Row.attachment,
            "id" | "key" | "name" | "size" | "type"
          >[]
          channelId: string
          content: string
          now?: number | undefined
          subscriptionId?: string | undefined
        },
      ) {
        const now = args.now ?? Date.now()
        assertIsLoggedIn(authData)
        await assertChannelPermission(
          tx,
          authData,
          args.channelId,
          "channel.send_message",
        )

        await tx.mutate.message.insert({
          account_id: authData.id,
          channel_id: args.channelId,
          content: args.content,
          id: args.id,
          inserted_at: now,
          updated_at: now,
        })
        await Promise.all(
          args.attachments.map((attachment) =>
            tx.mutate.attachment.insert({
              ...attachment,
              inserted_at: now,
              message_id: args.id,
              updated_at: now,
            }),
          ),
        )

        if (args.subscriptionId) {
          const subscription = await tx.query.channel_subscription
            .where("id", args.subscriptionId)
            .one()
            .run()
          if (subscription) {
            assert(
              subscription.account_id === authData.id,
              "forbidden:channel_subscription",
            )
            await tx.mutate.channel_subscription.update({
              id: subscription.id,
              last_read_at: now,
            })
          } else
            await tx.mutate.channel_subscription.insert({
              account_id: authData.id,
              channel_id: args.channelId,
              id: args.subscriptionId,
              inserted_at: now,
              last_read_at: now,
              subscribed_at: now,
              updated_at: now,
            })
        }
      },
      async delete(tx: Transaction, args: { id: string }) {
        assertIsLoggedIn(authData)
        const message = await tx.query.message.where("id", args.id).one().run()
        assert(message?.account_id === authData.id, "forbidden:message")
        await assertChannelPermission(
          tx,
          authData,
          message.channel_id,
          "channel.send_message",
        )
        const thread = await tx.query.thread
          .where("message_id", args.id)
          .one()
          .run()
        if (thread && thread.message_count > 0)
          // soft delete messages that have threads attached
          await tx.mutate.message.update({
            id: args.id,
            content: "",
            deleted_at: Date.now(),
          })
        else await tx.mutate.message.delete({ id: args.id })
      },
      async update(tx: Transaction, args: { id: string; content: string }) {
        assertIsLoggedIn(authData)
        const message = await tx.query.message.where("id", args.id).one().run()
        assert(message?.account_id === authData.id, "forbidden:message")
        await assertChannelPermission(
          tx,
          authData,
          message.channel_id,
          "channel.send_message",
        )
        await tx.mutate.message.update({
          id: args.id,
          content: args.content,
          updated_at: Date.now(),
        })
      },
    },
    reaction: {
      async create(
        tx: Transaction,
        args: { emoji: string; id: string; messageId: string },
      ) {
        const now = Date.now()
        assertIsLoggedIn(authData)
        const message = await tx.query.message
          .where("id", "=", args.messageId)
          .one()
          .run()
        assert(message, "forbidden:message")
        await tx.mutate.reaction.insert({
          account_id: authData.id,
          emoji: args.emoji,
          id: args.id,
          inserted_at: now,
          message_id: message.id,
          updated_at: now,
        })
      },
      async delete(tx: Transaction, args: { id: string }) {
        assertIsLoggedIn(authData)
        const reaction = await tx.query.reaction
          .where("id", "=", args.id)
          .one()
          .run()
        assert(reaction, "forbidden:reaction")
        await tx.mutate.reaction.delete({ id: reaction.id })
      },
    },
    thread: {
      async createMessage(
        tx: Transaction,
        args: {
          id: string
          attachments: Pick<
            t.Row.attachment,
            "id" | "key" | "name" | "size" | "type"
          >[]
          channelId: string
          content: string
          now?: number | undefined
          parentMessageId: string
          subscriptionId?: string | undefined
          threadId: string
        },
      ) {
        const now = args.now ?? Date.now()
        assertIsLoggedIn(authData)
        const parentMessage = await tx.query.message
          .where("id", "=", args.parentMessageId)
          .one()
          .run()
        assert(parentMessage, "forbidden:message")
        await assertChannelPermission(
          tx,
          authData,
          parentMessage.channel_id,
          "channel.send_message",
        )
        const thread = await tx.query.thread
          .where("message_id", args.parentMessageId)
          .one()
          .run()

        const threadId = thread?.id ?? args.threadId
        if (!thread)
          await tx.mutate.thread.insert({
            account_count: 0,
            id: threadId,
            inserted_at: now,
            message_count: 0,
            message_id: args.parentMessageId,
            updated_at: now,
          })

        await tx.mutate.message.insert({
          account_id: authData.id,
          channel_id: args.channelId,
          content: args.content,
          id: args.id,
          inserted_at: now,
          thread_id: threadId,
          updated_at: now,
        })
        await Promise.all(
          args.attachments.map((attachment) =>
            tx.mutate.attachment.insert({
              ...attachment,
              inserted_at: now,
              message_id: args.id,
              updated_at: now,
            }),
          ),
        )

        if (args.subscriptionId) {
          const subscription = await tx.query.thread_subscription
            .where("id", args.subscriptionId)
            .one()
            .run()
          if (subscription) {
            assert(
              subscription.account_id === authData.id,
              "forbidden:thread_subscription",
            )
            await tx.mutate.thread_subscription.update({
              id: subscription.id,
              last_read_at: now,
            })
          } else
            await tx.mutate.thread_subscription.insert({
              account_id: authData.id,
              thread_id: args.threadId,
              id: args.subscriptionId,
              inserted_at: now,
              last_read_at: now,
              subscribed_at: now,
              updated_at: now,
            })
        }
      },
    },
    thread_subscription: {
      async markRead(
        tx: Transaction,
        args: { id: string; channelId: string; now: number; threadId: string },
      ) {
        assertIsLoggedIn(authData)
        await assertChannelPermission(
          tx,
          authData,
          args.channelId,
          "channel.view",
        )

        const subscription = await tx.query.thread_subscription
          .where("id", args.id)
          .one()
          .run()
        if (subscription)
          await tx.mutate.thread_subscription.update({
            id: subscription.id,
            last_read_at: args.now,
          })
        else
          await tx.mutate.thread_subscription.upsert({
            account_id: authData.id,
            id: args.id,
            inserted_at: args.now,
            last_read_at: args.now,
            subscribed_at: args.now,
            thread_id: args.threadId,
            updated_at: args.now,
          })
      },
      async subscribe(
        tx: Transaction,
        args: { id: string; channelId: string; threadId: string },
      ) {
        const now = Date.now()
        assertIsLoggedIn(authData)
        await assertChannelPermission(
          tx,
          authData,
          args.channelId,
          "channel.view",
        )
        await tx.mutate.thread_subscription.upsert({
          account_id: authData.id,
          id: args.id,
          inserted_at: now,
          last_read_at: now,
          subscribed_at: now,
          thread_id: args.threadId,
          updated_at: now,
        })
      },
      async unsubscribe(tx: Transaction, args: { id: string }) {
        assertIsLoggedIn(authData)
        const subscription = await tx.query.thread_subscription
          .where("id", args.id)
          .one()
          .run()
        assert(
          subscription?.account_id === authData.id,
          "forbidden.thread_subscription",
        )
        await tx.mutate.thread_subscription.update({
          id: args.id,
          subscribed_at: null,
        })
      },
    },
  } as const satisfies zero.CustomMutatorDefs
}

export type mutators = ReturnType<typeof createMutators>

export type Transaction = zero.Transaction<schema>
